/**
 * Pull the message the backend actually sent out of a failed request.
 *
 * Every caller used to inline its own `error.response.data.message` ternary,
 * and each one read a single shape. The API does not have a single shape:
 *
 *   { message }            most REST controllers, and Blinker::Errors#handle_error
 *   { error }              controllers that render an exception directly,
 *                          e.g. Api::V3::VehicleLookupController#show
 *   { errors: [{ message }] }  GraphQL
 *
 * so a caller reading only `message` silently dropped a real reason and showed
 * its fallback instead -- which is where "Something went wrong" came from on the
 * VIN validation screen.
 *
 * `fallback` is still required: a network failure or an empty body has no
 * message to find, and the caller knows which screen it is on.
 */
/**
 * `error` is a sentence on some endpoints and a classification token on others,
 * and only the first kind belongs on a screen.
 *
 *   { error: "Could not find vehicle info" }   VehicleLookup -- show this
 *   { error: "validation error", message: … }  the VIN rules -- message wins
 *   { error: "422 Unprocessable Entity", … }   Blinker::Errors#json_for
 *
 * `message` is preferred above, so these only surface when it is missing or
 * blank -- and then "validation error" is the same class of useless string this
 * helper exists to stop showing. Status titles are matched by shape because the
 * list of them is long and fixed; the handful of bare classification words are
 * listed, because no pattern separates them from a short real sentence.
 */
const GENERIC_ERROR_TOKENS = [
  'error',
  'errors',
  'validation error',
  'validation errors',
  'bad request',
  'unprocessable entity',
  'internal server error',
]

const isSentence = (value: string): boolean => {
  const trimmed = value.trim()

  if (!trimmed) return false
  // "422 Unprocessable Entity", "500 Internal Server Error".
  if (/^\d{3}\s/.test(trimmed)) return false

  return !GENERIC_ERROR_TOKENS.includes(trimmed.toLowerCase())
}

export const getApiErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data

  if (!data) return fallback

  // A non-JSON body (an HTML error page from a proxy, say) arrives as a string.
  // It is never something to put in front of a person.
  if (typeof data !== 'string') {
    if (typeof data.message === 'string' && data.message.trim()) {
      return data.message
    }

    if (typeof data.error === 'string' && isSentence(data.error)) {
      return data.error
    }

    const graphqlMessage = data.errors?.[0]?.message
    if (typeof graphqlMessage === 'string' && graphqlMessage.trim()) {
      return graphqlMessage
    }
  }

  return fallback
}

export default getApiErrorMessage
